<script setup lang="ts">
  import { ref, onMounted, watch } from 'vue';
  import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';
  
  import {
    Chart as ChartJS,
    LineController,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Legend,
    Tooltip,
    ChartConfiguration,
    TimeScale,
    BarController,
    BarElement,
  ChartTypeRegistry,
  } from 'chart.js';
  ChartJS.register(
    LineController,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Legend,
    Tooltip,
    TimeScale,
    BarController,
    BarElement,
  )
  
  // init chart
  const chartCanvas = ref<HTMLCanvasElement | null>(null);
  let chartInstance: ChartJS | null = null;
  
  // props
  const props = defineProps<{
    chartList: {
      date: string
      countTotal: number
      seatTotalNo: number
      emptyRate: number
    }[],
    type: string
  }>()
  
  onMounted(() => {
    // set chart
    const chatDataList = props.chartList;
    const {
      dates,
      counts,
      seats,
      rates,
      cinemaCounts,
    } = getChartData(chatDataList);
    
  
    if (chartCanvas.value) {
      const chartConfig: ChartConfiguration = {
        type: props.type as keyof ChartTypeRegistry,
        data: {
          labels: dates,
          datasets: [
          {
              label: '上映館数',
              data: cinemaCounts,
              fill: false,
              borderColor: 'rgba(46, 204, 113, 1)',
              backgroundColor: 'rgba(46, 204, 113, 1)',
              borderWidth: 3,
              // tension: 0.5,
              yAxisID: 'y',
            },
            {
              label: '上映回数（合計）',
              data: counts,
              borderColor: 'rgba(249, 183, 73, 1)',
              backgroundColor: 'rgba(249, 183, 73, 1)',
              borderWidth: 3,
              // tension: 0.5,
              yAxisID: 'y1',
            },
            {
              label: '座席数（合計）',
              data: seats,
              fill: false,
              borderColor: 'rgba(249, 73, 105, 1)',
              backgroundColor: 'rgba(249, 73, 105, 1)',
              borderWidth: 3,
              // tension: 0.5,
              yAxisID: 'y2',
            },
            {
              label: '充足率 (ネット予約終了まで)',
              data: rates,
              fill: false,
              borderColor: 'rgba(73, 80, 249, 1)',
              backgroundColor: 'rgba(73, 80, 249, 1)',
              borderWidth: 3,
              // tension: 0.5,
              yAxisID: 'y3',
            },
          ],
        },
        options: {
          plugins: {
            tooltip: {
              callbacks: {
                label: function(tooltipItem) {
                  if (tooltipItem.dataset.yAxisID === 'y3') {
                    return `充足率 (ネット予約終了まで): ${tooltipItem.raw}%`;
                  }
                }
              }
            },
            legend: {
              display: true,
              onClick: function (e, legendItem) {
                // Toggle the hidden state of the dataset
                const datasetIndex = legendItem.datasetIndex;
                const dataset = chartInstance?.getDatasetMeta(datasetIndex);
                
                dataset.hidden = !dataset.hidden;
                // 触发更新
                const hidden = dataset?.hidden;
                const id = dataset?.vAxisID;
                if (chartInstance) {
                  chartInstance.options.scales[id].display = !hidden;
                }
                chartInstance!.update();
                
              }
            }
          },
          responsive: true,
          scales: {
            x: {
              display: false,
              type: 'time',
              time: {
                unit: 'day',
                tooltipFormat: 'YYYY/MM/DD',
                displayFormats: {
                  day: 'YYYY/MM/DD',
                  month: 'YYYY/MM',
                  year: 'YYYY',
                },
              },
              ticks: {
                autoSkip: true,
                maxRotation: 0
              },
              grid: {
                display: false,
              }
            },
            y: {
              beginAtZero: true,
              display: false,
              type: 'linear',
              position: 'left',
              grid: {
                offset: true,
                drawOnChartArea: false // 只显示左轴的网格线
              },
              ticks: {
                padding: 40,
                color: 'rgba(46, 204, 113, 1)',
                callback: function(value) {
                  const num = Number(value);
                  if (isNaN(num) || num < 0) return null;    // 过滤非法值
                  return (num % 1 === 0) ? num : null; // 正数仅保留整数
                }
              },
            },
            y1: {
              beginAtZero: true,
              display: false,
              type: 'linear',
              position: 'left',
              ticks: {
                padding: 20,
                color: 'rgba(249, 183, 73, 1)',
                callback: function(value) {
                  const num = Number(value);
                  if (isNaN(num) || num < 0) return null;    // 过滤非法值
                  return (num % 1 === 0) ? num : null; // 正数仅保留整数
                },
              },
            },
            y2: {
              beginAtZero: true,
              display: false,
              type: 'linear',
              position: 'left',
              grid: {
                offset: true,
                drawOnChartArea: false // 只显示左轴的网格线
              },
              ticks: {
                padding: 40,
                color: 'rgba(249, 73, 105, 1)',
                callback: function(value) {
                  const num = Number(value);
                  if (isNaN(num) || num < 0) return null;    // 过滤非法值
                  return (num % 1 === 0) ? num : null; // 正数仅保留整数
                }
              },
            },
            y3: {
              beginAtZero: true,
              display: false,
              type: 'linear',
              position: 'right',
              grid: {
                drawOnChartArea: false // 只显示左轴的网格线
              },
              ticks: {
                color: 'rgba(73, 80, 249, 1)',
                padding: 20,
                callback: function(value) {
                  const num = Number(value);
                  if (isNaN(num) || num < 0) return null; // 非数值直接返回
  
                  // 如果是整数，返回无小数
                  if (Number.isInteger(num)) {
                    return num.toString() + '%';
                  }
  
                  // 格式化为两位小数，并去掉末尾的零
                  const formatted = num.toFixed(2);
                  const trimmed = formatted.replace(/\.?0+$/, '');
                  return trimmed + '%';
                }
              }
            },
          }
        },
      }
      chartInstance = new ChartJS(chartCanvas.value, chartConfig);
    }
  })
  
  // methods
  const getChartData = (list: any[] = []) => {
    const dates = list.map((item: { date: string }) => item.date);
    const cinemaCounts = list.map((item: { cinemaTotalNo: number }) => item.cinemaTotalNo);
    const counts = list.map((item: { countTotal: number }) => item.countTotal);
    const seats = list.map((item: { seatTotalNo: number }) => item.seatTotalNo);
    const rates = list.map((item: { emptyRate: number}) => item.emptyRate);
  
    return {
      dates,
      counts,
      seats,
      rates,
      cinemaCounts,
    }
  }
  // watch
  watch(() => props.chartList, (newItems, oldItems) => {
    if (newItems.length > 0) {
      // chartInstance!.options.scales.x?.display = true;
      // chartInstance!.options.scales.y?.display = true;
      // chartInstance!.options.scales.y1?.display = true;
      // chartInstance!.options.scales.y2?.display = true;
      chartInstance!.options!.scales!.x!.display = true;
      chartInstance!.options!.scales!.y!.display = true;
      chartInstance!.options!.scales!.y1!.display = true;
      chartInstance!.options!.scales!.y2!.display = true;
      chartInstance!.options!.scales!.y3!.display = true;
    } else {
      chartInstance!.options!.scales!.x!.display = false;
      chartInstance!.options!.scales!.y!.display = false;
      chartInstance!.options!.scales!.y1!.display = false;
      chartInstance!.options!.scales!.y2!.display = false;
      chartInstance!.options!.scales!.y3!.display = false;
    }
    const {
      dates,
      counts,
      seats,
      rates,
      cinemaCounts,
    } = getChartData(newItems);
  
    chartInstance!.data.labels = dates;
    chartInstance!.data.datasets[0].data = cinemaCounts;
    chartInstance!.data.datasets[1].data = counts;
    chartInstance!.data.datasets[2].data = seats;
    chartInstance!.data.datasets[3].data = rates;
    chartInstance!.update();
  }, { deep: true })
  
  </script>
  <template>
    <div class="chart-container">
      <canvas ref="chartCanvas"></canvas>
    </div>
  </template>
  <style lang="less" scoped>
  .chart-container {
      width: 100%;
      margin-bottom: 60px;
      border: 1px solid #E9E9E9;
      border-radius: 5px;
      box-sizing: border-box;
      padding: 20px 15px;
    }
  </style>